import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiChevronDown, mdiChevronUp, mdiLoading, mdiPlay, mdiSitemapOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { ActionComposeDialog } from '../issues/ActionComposeDialog'
import { NoticeDialog } from '../issues/NoticeDialog'
import { proofLabel } from '../issues/issueFamily'
import { useCan } from '../../contexts/PermissionsContext'
import {
    formatSeconds,
    labelIn,
    useCancelScenario,
    useDecide,
    useIssueScenarios,
    useScenarioPreview,
    useScenarioRun,
    useStartScenario,
    type ApiError,
    type ScenarioPreview,
    type ScenarioRun,
} from '../../hooks/useScenariosApi'

type IssueLike = {
    uid: string
    status: string
    parent_issue_uid?: string | null
    properties?: Record<string, unknown> | null
}

/** The kind of a scenario step action, as the step card acts on it. */
const SEND_EMAIL_ACTIONS = new Set([
    'order_diagnosis', 'request_document', 'chase_provider', 'renegotiate', 'investigate_replacement', 'change_provider',
    'dunning', 'order_works', 'request_quotes', 'send_message', 'formal_notice', 'award_contract', 'send_lease_letter',
])

/** A missing-data code (`no_service_holder:leak_detection`) as a sentence. */
export function missingText(t: (k: string, o?: Record<string, unknown>) => string, code?: string | null): string {
    if (!code) return ''
    const [key, service] = code.split(':')
    return t(`scenarios:missing.${key}`, {
        defaultValue: key,
        service: service ? t(`scenarios:services.${service}`, { defaultValue: service }) : '',
    })
}

/** A due source (`contract:intervention_time=4 HOUR`) as a sentence. */
export function dueSourceText(t: (k: string, o?: Record<string, unknown>) => string, source?: string | null): string {
    if (!source) return ''
    const [kind, rest = ''] = source.split(/:(.*)/s)
    const [name, value = ''] = rest.split('=')
    const pretty = value.replace(/ HOUR$/, ' h').replace(/ DAY$/, ' j').replace(/ WORKING_DAY$/, ' j ouvrés').replace(/ MONTH$/, ' mois')
    if (kind === 'days') return t('scenarios:due_source.days', { value: rest })
    if (kind === 'date') return t('scenarios:due_source.date', { name: rest })
    return t(`scenarios:due_source.${kind}`, { name, value: pretty, defaultValue: source })
}

function errorText(t: (k: string, o?: Record<string, unknown>) => string, e: unknown): string {
    const code = (e as ApiError | null)?.code
    return code ? t(`scenarios:errors.${code}`, { defaultValue: (e as ApiError).title ?? code }) : t('scenarios:errors.generic')
}

/**
 * The remediation scenario of a ticket (spec 2026-10-28-15 § 10): the
 * « Scénario » menu and its preview on a ticket without one, the scenario card
 * on a dossier.
 */
export function ScenarioPanel({ orgCode, issue }: { orgCode: string; issue: IssueLike }) {
    const { data: run, isLoading } = useScenarioRun(orgCode, issue.uid)
    if (isLoading) return null
    if (run?.run) return <ScenarioCard orgCode={orgCode} dossierUid={issue.uid} run={run} />
    if (issue.parent_issue_uid || issue.status === 'closed') return null
    return <ScenarioStartMenu orgCode={orgCode} issueUid={issue.uid} />
}

function ScenarioStartMenu({ orgCode, issueUid }: { orgCode: string; issueUid: string }) {
    const { t, i18n } = useTranslation('scenarios')
    const canWrite = useCan('issues.write')
    const [open, setOpen] = useState(false)
    const [previewing, setPreviewing] = useState<string | null>(null)
    const { data: candidates = [] } = useIssueScenarios(orgCode, issueUid, open)
    if (!canWrite) return null
    const matched = candidates.filter((c) => c.matched)
    const others = candidates.filter((c) => !c.matched)
    const row = (c: (typeof candidates)[number]) => (
        <li key={c.scenario.code}>
            <button
                type="button"
                className="w-full rounded px-2 py-1 text-left text-sm hover:bg-muted"
                onClick={() => setPreviewing(c.scenario.code)}
                data-testid={`scenario-candidate-${c.scenario.code}`}
            >
                {labelIn(c.scenario.label, i18n.language, c.scenario.code)}
                {c.scenario.origin === 'organization' && (
                    <Badge variant="outline" className="ml-2 text-[10px]">{t('origin.organization')}</Badge>
                )}
            </button>
        </li>
    )
    return (
        <Card data-testid="scenario-start-panel">
            <CardContent className="pt-4">
                <Button variant="outline" size="sm" onClick={() => setOpen((v) => !v)} data-testid="scenario-start-menu">
                    <AppIcon path={mdiSitemapOutline} size="sm" />
                    {t('ticket.menu')}
                    <AppIcon path={open ? mdiChevronUp : mdiChevronDown} size="sm" />
                </Button>
                {open && (
                    <div className="mt-3 space-y-3" data-testid="scenario-candidates">
                        {matched.length > 0 && (
                            <div>
                                <p className="text-xs font-medium text-muted-foreground">{t('ticket.matched')}</p>
                                <ul className="mt-1">{matched.map(row)}</ul>
                            </div>
                        )}
                        {others.length > 0 && (
                            <div>
                                <p className="text-xs font-medium text-muted-foreground">{t('ticket.others')}</p>
                                <ul className="mt-1 max-h-48 overflow-auto">{others.map(row)}</ul>
                            </div>
                        )}
                        {candidates.length === 0 && <p className="text-sm text-muted-foreground">{t('ticket.none')}</p>}
                    </div>
                )}
            </CardContent>
            {previewing && (
                <ScenarioPreviewDialog orgCode={orgCode} issueUid={issueUid} code={previewing} onClose={() => setPreviewing(null)} />
            )}
        </Card>
    )
}

function ScenarioPreviewDialog({ orgCode, issueUid, code, onClose }: { orgCode: string; issueUid: string; code: string; onClose: () => void }) {
    const { t, i18n } = useTranslation('scenarios')
    const preview = useScenarioPreview(orgCode, issueUid)
    const start = useStartScenario(orgCode, issueUid)
    const { mutate } = preview
    useEffect(() => {
        mutate(code)
    }, [code, mutate])
    const p = preview.data
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-2xl" data-testid="scenario-preview-dialog">
                <DialogHeader>
                    <DialogTitle>{t('ticket.preview_title', { label: labelIn(p?.label, i18n.language, code) })}</DialogTitle>
                    <DialogDescription>{t('ticket.preview_description')}</DialogDescription>
                </DialogHeader>
                {preview.isPending && <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin" />}
                {p && <PreviewSteps preview={p} orgCode={orgCode} />}
                {start.error && <p className="text-sm text-destructive" data-testid="scenario-start-error">{errorText(t, start.error)}</p>}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{t('card.cancel_confirm')}</Button>
                    <Button
                        onClick={() => start.mutate(code, { onSuccess: onClose })}
                        disabled={start.isPending || !p}
                        data-testid="scenario-start"
                    >
                        <AppIcon path={mdiPlay} size="sm" />
                        {t('ticket.start')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

/** The steps of a preview: state at start, recipients, due date and proof. */
export function PreviewSteps({ preview, orgCode }: { preview: ScenarioPreview; orgCode: string }) {
    const { t, i18n } = useTranslation('scenarios')
    const { t: ti } = useTranslation('issues')
    return (
        <ol className="max-h-[60vh] space-y-2 overflow-auto">
            {preview.steps.map((st) => (
                <li key={st.code} className="rounded border border-border p-2 text-sm" data-testid={`scenario-preview-step-${st.code}`}>
                    <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">{labelIn(st.label, i18n.language, st.code)}</span>
                        <Badge variant="outline" className="text-[11px]" data-testid={`scenario-preview-state-${st.code}`}>
                            {st.state === 'skipped'
                                ? t('state.skipped', { reason: missingText(t, st.reason) })
                                : st.state === 'draft'
                                    ? t('state.draft', { steps: (st.waits_on ?? []).join(', ') })
                                    : t('state.active')}
                        </Badge>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground" data-testid={`scenario-preview-recipients-${st.code}`}>
                        {st.resolver && st.resolver !== 'internal' ? (
                            st.candidates.length > 0 ? (
                                <>
                                    {t('preview.recipients')}: {st.candidates.map((c) => `${c.name || c.email} (${t(`reasons.${c.reason}`, { defaultValue: c.reason })})`).join(', ')}
                                    {st.total && st.total > st.candidates.length ? ` ${t('preview.more', { count: st.total - st.candidates.length })}` : ''}
                                </>
                            ) : (
                                <span className="text-warning" data-testid={`scenario-preview-missing-${st.code}`}>
                                    {missingText(t, st.missing)}
                                    {st.missing_fix && (
                                        <a className="ml-1 underline" href={`/${orgCode}${st.missing_fix}`}>{t('preview.fix')}</a>
                                    )}
                                </span>
                            )
                        ) : (
                            t('preview.no_recipient')
                        )}
                    </div>
                    {st.due_date && (
                        <div className="text-xs text-muted-foreground" data-testid={`scenario-preview-due-${st.code}`}>
                            {t('preview.due')}: {new Date(st.due_date).toLocaleString(i18n.language)} · {dueSourceText(t, st.due_source)}
                        </div>
                    )}
                    {st.follow_up_source && (
                        <div className="text-xs text-muted-foreground" data-testid={`scenario-preview-follow-up-${st.code}`}>
                            {t('preview.follow_up', { source: dueSourceText(t, st.follow_up_source) })}
                        </div>
                    )}
                    {st.wait_for && (
                        <div className="text-xs text-muted-foreground">{t('preview.proof', { proof: proofLabel(ti, st.wait_for) })}</div>
                    )}
                </li>
            ))}
        </ol>
    )
}

type RunStep = ScenarioRun['steps'][number]

function ScenarioCard({ orgCode, dossierUid, run }: { orgCode: string; dossierUid: string; run: ScenarioRun }) {
    const { t, i18n } = useTranslation('scenarios')
    const { t: ti } = useTranslation('issues')
    const canWrite = useCan('issues.write')
    const cancel = useCancelScenario(orgCode, dossierUid)
    const [cancelling, setCancelling] = useState(false)
    const [reason, setReason] = useState('')
    const [sending, setSending] = useState<RunStep | null>(null)
    const [noticing, setNoticing] = useState<RunStep | null>(null)
    const [deciding, setDeciding] = useState<RunStep | null>(null)
    const running = run.run.status === 'running'
    const label = labelIn(run.label, i18n.language, run.run.scenario_code)
    const current = run.steps.find((s) => s.code === run.current_step)
    const scenarioMeasures = run.measures.filter((m) => m.kind === 'scenario')
    const stepMeasures = run.measures.filter((m) => m.kind !== 'scenario' && m.seconds != null)
    return (
        <Card data-testid="scenario-card">
            <CardHeader className="pb-2">
                <CardTitle className="flex flex-wrap items-center gap-2 text-base">
                    <AppIcon path={mdiSitemapOutline} size="sm" />
                    <span data-testid="scenario-card-label">{t('card.title', { label })}</span>
                    <Badge variant="outline" className="text-[11px]" data-testid="scenario-card-version">{t('card.version', { version: run.run.scenario_version })}</Badge>
                    {!running && (
                        <Badge variant="outline" className="text-[11px]" data-testid="scenario-card-status">
                            {t(run.run.status === 'completed' ? 'card.completed' : 'card.cancelled')}
                        </Badge>
                    )}
                </CardTitle>
                {current && (
                    <p className="text-sm text-muted-foreground" data-testid="scenario-current-step">
                        {t('card.current_step', { step: labelIn(current.label, i18n.language, current.code) })}
                    </p>
                )}
            </CardHeader>
            <CardContent className="space-y-3">
                <ol className="space-y-1.5">
                    {run.steps.map((st) => {
                        const open = st.status === 'open' || st.status === 'in_progress'
                        const isDecision = (st.options && st.options.length > 0) || st.options_from === 'quotes'
                        return (
                            <li key={st.code} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`scenario-step-${st.code}`} data-status={st.status}>
                                <span className="min-w-0 flex-1">
                                    {st.issue_uid ? (
                                        <Link to="/$org/issues/$uid" params={{ org: orgCode, uid: st.issue_uid }} className="hover:underline">
                                            {labelIn(st.label, i18n.language, st.code)}
                                        </Link>
                                    ) : (
                                        labelIn(st.label, i18n.language, st.code)
                                    )}
                                    {open && st.wait_for && (
                                        <span className="ml-2 text-xs text-muted-foreground" data-testid={`scenario-step-waiting-${st.code}`}>
                                            {t('card.waiting', { proof: proofLabel(ti, st.wait_for) })}
                                        </span>
                                    )}
                                    {st.missing && open && (
                                        <span className="ml-2 text-xs text-warning">{t('card.missing', { what: missingText(t, st.missing) })}</span>
                                    )}
                                    {st.status === 'closed' && st.skipped_reason && (
                                        <span className="ml-2 text-xs text-muted-foreground">{t('state.skipped', { reason: missingText(t, st.skipped_reason) })}</span>
                                    )}
                                    {st.due_date && open && (
                                        <span className="ml-2 text-xs text-muted-foreground" data-testid={`scenario-step-due-source-${st.code}`}>
                                            {new Date(st.due_date).toLocaleDateString(i18n.language)} · {dueSourceText(t, st.due_source)}
                                        </span>
                                    )}
                                </span>
                                <Badge variant="outline" className="text-[11px]">
                                    {st.status === 'draft' ? t('state.draft_status') : t(`state.${st.status}`, { defaultValue: st.status })}
                                </Badge>
                                {running && canWrite && open && st.issue_uid && SEND_EMAIL_ACTIONS.has(st.action ?? '') && (
                                    <Button size="sm" variant="outline" onClick={() => setSending(st)} data-testid={`scenario-step-send-${st.code}`}>
                                        {t('card.send')}
                                    </Button>
                                )}
                                {running && canWrite && open && st.issue_uid && st.action === 'notify_occupants' && (
                                    <Button size="sm" variant="outline" onClick={() => setNoticing(st)} data-testid={`scenario-step-notify-${st.code}`}>
                                        {t('card.notify')}
                                    </Button>
                                )}
                                {running && canWrite && open && isDecision && (
                                    <Button size="sm" variant="outline" onClick={() => setDeciding(st)} data-testid={`scenario-decision-${st.code}`}>
                                        {t('card.decide')}
                                    </Button>
                                )}
                            </li>
                        )
                    })}
                </ol>
                <div data-testid="scenario-measures">
                    <p className="text-xs font-medium text-muted-foreground">{t('card.delays')}</p>
                    <ul className="mt-1 grid grid-cols-1 gap-x-4 text-xs sm:grid-cols-2">
                        {scenarioMeasures.map((m) => (
                            <li key={m.code} data-testid={`scenario-measure-${m.code}`}>
                                {t(`measures.${m.code}`, { defaultValue: m.code })} : {m.seconds != null ? formatSeconds(m.seconds, t) : t('card.measure_none')}
                            </li>
                        ))}
                        {stepMeasures.map((m) => (
                            <li key={`${m.step}-${m.code}`} className="text-muted-foreground">
                                {m.step} · {t(`measures.${m.code}`, { defaultValue: m.code })} : {formatSeconds(m.seconds, t)}
                            </li>
                        ))}
                    </ul>
                </div>
                {running && canWrite && (
                    <Button size="sm" variant="ghost" onClick={() => setCancelling(true)} data-testid="scenario-cancel">
                        {t('card.cancel')}
                    </Button>
                )}
            </CardContent>
            {sending?.issue_uid && (
                <ActionComposeDialog
                    orgCode={orgCode}
                    issueUid={sending.issue_uid}
                    issueTitle={labelIn(sending.label, i18n.language, sending.code)}
                    action={sending.action ?? 'send_message'}
                    open
                    onOpenChange={(o) => !o && setSending(null)}
                />
            )}
            {noticing?.issue_uid && (
                <NoticeDialog
                    orgCode={orgCode}
                    issueUid={noticing.issue_uid}
                    open
                    onOpenChange={(o) => !o && setNoticing(null)}
                />
            )}
            {deciding && <DecisionDialog orgCode={orgCode} dossierUid={dossierUid} step={deciding} onClose={() => setDeciding(null)} />}
            <Dialog open={cancelling} onOpenChange={setCancelling}>
                <DialogContent data-testid="scenario-cancel-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('card.cancel')}</DialogTitle>
                    </DialogHeader>
                    <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('card.cancel_reason')} data-testid="scenario-cancel-reason" />
                    <DialogFooter>
                        <Button
                            variant="destructive"
                            onClick={() => cancel.mutate(reason, { onSuccess: () => setCancelling(false) })}
                            disabled={cancel.isPending}
                            data-testid="scenario-cancel-confirm"
                        >
                            {t('card.cancel_confirm')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    )
}

function DecisionDialog({ orgCode, dossierUid, step, onClose }: { orgCode: string; dossierUid: string; step: RunStep; onClose: () => void }) {
    const { t, i18n } = useTranslation('scenarios')
    const decide = useDecide(orgCode, dossierUid)
    const [option, setOption] = useState('')
    const [note, setNote] = useState('')
    const [contact, setContact] = useState('')
    const options = step.options_from === 'quotes'
        ? (step.quotes ?? []).map((q) => ({ code: q.document_uid ?? '', label: { en: q.name ?? '', fr: q.name ?? '' }, needs_note: false }))
        : (step.options ?? []).map((o) => ({ code: o.code ?? '', label: o.label, needs_note: o.needs_note }))
    const chosen = options.find((o) => o.code === option)
    const needsNote = Boolean(chosen?.needs_note)
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid={`scenario-decision-dialog-${step.code}`}>
                <DialogHeader>
                    <DialogTitle>{t('decision.title', { step: labelIn(step.label, i18n.language, step.code) })}</DialogTitle>
                </DialogHeader>
                <div className="space-y-2">
                    {options.map((o) => (
                        <label key={o.code} className="flex items-center gap-2 text-sm">
                            <input
                                type="radio"
                                name={`decision-${step.code}`}
                                checked={option === o.code}
                                onChange={() => setOption(o.code)}
                                data-testid={`scenario-decision-option-${o.code}`}
                            />
                            {labelIn(o.label, i18n.language, o.code)}
                        </label>
                    ))}
                    {step.options_from === 'quotes' && (
                        <Input value={contact} onChange={(e) => setContact(e.target.value)} placeholder={t('decision.contact')} data-testid="scenario-decision-contact" />
                    )}
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('decision.note')} data-testid="scenario-decision-note" />
                    {needsNote && !note.trim() && <p className="text-xs text-muted-foreground">{t('decision.note_required')}</p>}
                    {decide.error && <p className="text-sm text-destructive">{errorText(t, decide.error)}</p>}
                </div>
                <DialogFooter>
                    <Button
                        onClick={() => decide.mutate(
                            { step: step.code, option, note: note || undefined, document_uid: step.options_from === 'quotes' ? option : undefined, contact_uid: contact || undefined },
                            { onSuccess: onClose },
                        )}
                        disabled={!option || (needsNote && !note.trim()) || decide.isPending}
                        data-testid="scenario-decision-confirm"
                    >
                        {t('decision.confirm')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
